/**
 * Desktop product gallery loop arrows
 * ----------------------------------------------------------------------------
 * Adds prev/next chevrons overlaid on the desktop PDP gallery carousel. They
 * appear on hover/focus (handled in CSS) and loop continuously: clicking next
 * on the last image returns to the first, and vice versa.
 *
 * Navigation is driven through the <scroll-carousel> public API
 * (`select(index)`, `selectedIndex`, `cells`) rather than its built-in
 * `next()/previous()`. Those clamp at the ends AND back mobile swipe, which
 * must keep clamping — so we wrap here with modulo instead of touching them.
 *
 * `carousel.cells` already excludes variant-filtered (hidden) media, so the
 * wrap math reads the live visible-image count and adapts automatically.
 */
(() => {
  const WRAPPER_SELECTOR = '.product-gallery__carousel-wrapper';
  const SINGLE_CLASS = 'product-gallery__carousel-wrapper--single';

  function visibleCount(carousel) {
    // Undefined until the custom element is upgraded.
    return carousel.cells ? carousel.cells.length : null;
  }

  function setup(wrapper) {
    if (wrapper.dataset.loopArrowsReady) return;

    const carousel = wrapper.querySelector('scroll-carousel');
    const prev = wrapper.querySelector('[data-gallery-loop-prev]');
    const next = wrapper.querySelector('[data-gallery-loop-next]');
    if (!carousel || !prev || !next) return;

    wrapper.dataset.loopArrowsReady = 'true';

    const go = (delta) => {
      const count = visibleCount(carousel);
      if (!count || count < 2) return;
      const index = (carousel.selectedIndex + delta + count) % count;
      carousel.select(index);
    };

    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));

    // Hide the controls when only one image is visible (e.g. a variant with a
    // single photo). Re-evaluated on selection and whenever media is filtered.
    const syncVisibility = () => {
      const count = visibleCount(carousel);
      if (count === null) return;
      wrapper.classList.toggle(SINGLE_CLASS, count < 2);
    };

    carousel.addEventListener('carousel:select', syncVisibility);
    new MutationObserver(syncVisibility).observe(carousel, {
      attributes: true,
      attributeFilter: ['hidden'],
      subtree: true,
    });

    // Wait for the carousel to be defined before the first visibility check so
    // we never hide the arrows just because the element hasn't upgraded yet.
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('scroll-carousel').then(syncVisibility);
    } else {
      syncVisibility();
    }
  }

  function init() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when a section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
